import { createSlice } from '@reduxjs/toolkit';

import {
  estelamForeignNationalityThunk,
  estelamSabtAhvalThunk,
} from 'Feature/Common/Common/Actions/Thunks/estelamSabtAhvalThunk';
import { getAddressWithPostCodeThunk } from 'Feature/Common/Common/Actions/Thunks/getAddressWithPostCodeThunk';
import { getAllCountries } from 'Feature/Khodro/ShakhsSales/Thunks/getAllCountries';
import { getAllTarhHayeSodoorByNoeBimeh } from 'Feature/Khodro/ShakhsSales/Thunks/getAllTarhHayeSodoorByNoeBimeh';
import { createShenasehPardakht } from 'Feature/Mosaferati/Common/Actions/action';
import LabelValueType from 'Shared/Core/Common/Models/LabelValueType';
import { ClearAllToasts } from 'Shared/Toast';
import { gregorianDateTime } from 'Shared/Utils/Extensions/String/StringExtensions';

import { ITarhSafarLabelValueType } from '../Components/02-StepTwo/Models/ITarhSafarLabelValueType';

import AddressInitialState from './InitialStates/AddressInitialState';
import { BimehNamehInitialState } from './InitialStates/BimehNamehInitialState';
import BimehShodehInitialState from './InitialStates/BimehShodehInitialState';
import {
  addBimehNamehMosaferatiToOrder,
  estelamMosaferati,
  getActiveTarhHayeSafarByNoeGharardadSafarCoreIdAndCountryCoreId,
  getDestinationCountry,
  getNoeGharardadSafarCountryByCountryCoreId,
} from './action';
import { IMosaferatiEstelamState } from './model';

import ITarafGharardadDto from 'Feature/Mosaferati/Common/types/Commands/Sodoor/ITarafGharardadDto';

const initialState: IMosaferatiEstelamState = {
  wizard: {
    beginStep: 1,
    endStep: 6,
    currentStep: 1,
  },
  estelamAndSodoorData: {
    bimehGozar: null,
    bimehShodeh: null,
  },
  firstStepData: null,
  secondStepData: null,
  estelam: {
    loading: false,
    command: null,
    result: null,
    sodoor: null,
  },
  destinationCountries: {
    loading: false,
    data: undefined,
  },
  nationalities: {
    loading: false,
    data: undefined,
  },
  tarhHayeSafar: {
    loading: false,
    data: null,
  },
  tarhHayeSodoor: {
    loading: false,
    data: null,
  },
  noeGharardadSafar: {
    loading: false,
    data: null,
  },
  isSuccess: false,
  haghBimehPardakhti: 0,

  editMood: false,
  showConfirmFromUser: false,
  isMablaghKharidBimehNamehMoreThanHaghBimeh: false,
  getCodeShenasehPardakhtMosaferatiCommand: {
    bimehNameh: BimehNamehInitialState,
    address: AddressInitialState,
    bimehShodeh: BimehShodehInitialState,
    tarafHayeGharardad: null,
    tarhHayeSodoor: [],
  },
  tarafHayeGharardad: [],

  getCodeShenasehPardakhtMosaferatiResult: {
    loading: false,
    data: null,
  },
  codeposti: {
    loading: false,
  },
  addToOrderResponse: {
    loading: false,
  },
  estelamSabtAhval: {
    loading: false,
  },
  estelamForeignNationality: {
    loading: false,
    isSuccess: false,
  },
};

export const mosaferatiEstelamSlice = createSlice({
  name: 'Mosaferati/Estelam',
  initialState,
  reducers: {
    setStepOneData: (state, action) => {
      state.firstStepData = action.payload;
    },
    setStepTwoData: (state, action) => {
      state.secondStepData = action.payload;
    },
    clearStepsData: (state) => {
      state.firstStepData = initialState.firstStepData;
      state.secondStepData = initialState.secondStepData;
      state.estelamAndSodoorData = initialState.estelamAndSodoorData;
      state.wizard = initialState.wizard;
    },
    setCommandData: (state, action) => {
      state.estelam.command = action.payload;
    },
    setSodoorData: (state, action) => {
      state.estelam.sodoor = action.payload;
    },
    clearEstelamResult: (state) => {
      state.estelam.result = null;
    },
    setTarafHayeGharardad: (state, action) => {
      state.tarafHayeGharardad = action.payload;
      state.getCodeShenasehPardakhtMosaferatiCommand.tarafHayeGharardad = action.payload.map(
        (taraf: ITarafGharardadDto) => ({
          ...taraf,
          tarikhTavalod: gregorianDateTime(taraf.tarikhTavalod),
        })
      );
    },
    setBimehShodeh: (state, action) => {
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.codeMelli = action.payload.codeMelli;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.naamKhanevadegi = action.payload.naamKhanevadegi;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.naam = action.payload.naam;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.tarikhTavalod = gregorianDateTime(
        action.payload.tarikhTavalod
      );
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.jensiat = action.payload.jensiat;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.melliatCoreId = action.payload?.melliatCoreId;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.naamKhanevadegiLatin =
        action.payload.naamKhanevadegiLatin;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.naamLatin = action.payload.naamLatin;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.codeRahgiriVisa = action.payload.codeRahgiriVisa;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.shomarehPassport = action.payload?.shomarehPassport;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.tarikhTavalodMiladi = gregorianDateTime(
        action.payload?.tarikhTavalodMiladi
      );
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.shenasehMelliHoghooghi =
        action.payload?.shenasehMelliHoghooghi;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.isBimehgozar = action.payload?.isBimehgozar;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.isTarafAsli = action.payload?.isTarafAsli;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.isPardakhtKonandeh =
        action.payload?.isPardakhtKonandeh;
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh.haghighiHoghooghi = action.payload?.haghighiHoghooghi;
    },
    setStepSixModel: (state, action) => {
      state.getCodeShenasehPardakhtMosaferatiCommand.address.codePosti = action.payload.codePosti;
      state.getCodeShenasehPardakhtMosaferatiCommand.address.mobile = action.payload.mobile;
      state.getCodeShenasehPardakhtMosaferatiCommand.address.telephone = action.payload?.tel;
      state.getCodeShenasehPardakhtMosaferatiCommand.address.neshani = action.payload.address;
      state.getCodeShenasehPardakhtMosaferatiCommand.address.cityCoreId = action.payload.cityCoreId;
      state.getCodeShenasehPardakhtMosaferatiCommand.address.email = action.payload?.email;
    },
    clearBimehShode: (state) => {
      state.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh =
        initialState.getCodeShenasehPardakhtMosaferatiCommand.bimehShodeh;
    },
    clearSodoorCommand: (state) => {
      state.getCodeShenasehPardakhtMosaferatiCommand = initialState.getCodeShenasehPardakhtMosaferatiCommand;
      state.wizard = initialState.wizard;
      state.editMood = false;
      state.isSuccess = false;
      state.showConfirmFromUser = false;
    },
    clearGetCodeShenasehPardakhtMosaferatiResult: (state) => {
      state.getCodeShenasehPardakhtMosaferatiResult.data = null;
    },
    setBimehGozarData: (state, action) => {
      state.estelamAndSodoorData.bimehGozar = action.payload;
    },
    setBimehShodehData: (state, action) => {
      state.estelamAndSodoorData.bimehShodeh = action.payload;
    },
    setShowConfirmFromUser: (state, action) => {
      state.showConfirmFromUser = action.payload;
    },
    setNoeGharardadSafar: (state, action) => {
      state.noeGharardadSafar = action.payload;
    },
    setTarhHayeSafar: (state, action) => {
      state.tarhHayeSafar = action.payload;
    },
    setTarhHayeSodoor: (state, action) => {
      state.tarhHayeSodoor = action.payload;
    },
    setIsMablaghKharidBimehNamehMoreThanHaghBimeh: (state, action) => {
      state.isMablaghKharidBimehNamehMoreThanHaghBimeh = action.payload;
    },
    wizardNextStep: (state) => {
      let nextStep = 0;
      if (state.wizard.currentStep === 3 && state.estelamAndSodoorData.bimehGozar?.isBimehShodeh) {
        nextStep = state.wizard.currentStep + 2;
      } else {
        nextStep = state.wizard.currentStep + 1;
      }

      if (nextStep <= state.wizard.endStep) {
        state.wizard.currentStep = nextStep;
      }
      ClearAllToasts();
    },
    wizardPreviousStep: (state) => {
      let previousStep = 0;
      if (state.wizard.currentStep === 5 && state.estelamAndSodoorData.bimehGozar?.isBimehShodeh) {
        previousStep = state.wizard.currentStep - 2;
      } else {
        previousStep = state.wizard.currentStep - 1;
      }
      if (previousStep >= state.wizard.beginStep) {
        state.wizard.currentStep = previousStep;
      }
      ClearAllToasts();
    },
    setHaghBimehPardakhtiCommand: (state, action) => {
      state.haghBimehPardakhti = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDestinationCountry.pending, (state) => {
        state.destinationCountries.loading = true;
      })
      .addCase(getDestinationCountry.fulfilled, (state, action) => {
        state.destinationCountries.loading = false;
        if (action.payload !== undefined) {
          const list: LabelValueType[] = action.payload?.map((item: any) => ({
            value: item.coreId,
            label: item.displayTitle,
          }));
          state.destinationCountries.data = list;
        }
      })
      .addCase(getDestinationCountry.rejected, (state) => {
        state.destinationCountries.loading = false;
      });

    builder
      .addCase(getAllCountries.pending, (state) => {
        state.nationalities.loading = true;
      })
      .addCase(getAllCountries.fulfilled, (state, action) => {
        state.nationalities.loading = false;
        if (action.payload !== undefined) {
          const list: LabelValueType[] = action.payload?.map((item: any) => ({
            value: item.coreId,
            label: item.displayTitle,
          }));
          state.nationalities.data = list;
        }
      })
      .addCase(getAllCountries.rejected, (state) => {
        state.nationalities.loading = false;
      });

    builder
      .addCase(getAllTarhHayeSodoorByNoeBimeh.pending, (state) => {
        state.tarhHayeSodoor.loading = true;
      })
      .addCase(getAllTarhHayeSodoorByNoeBimeh.fulfilled, (state, action) => {
        state.tarhHayeSodoor.loading = false;
        if (action.payload !== undefined) {
          const list: LabelValueType[] = action.payload?.map((item: any) => ({
            value: item.coreId,
            label: item.displayTitle,
          }));
          state.tarhHayeSodoor.data = list;
        }
      })
      .addCase(getAllTarhHayeSodoorByNoeBimeh.rejected, (state) => {
        state.tarhHayeSodoor.loading = false;
      });

    builder
      .addCase(getActiveTarhHayeSafarByNoeGharardadSafarCoreIdAndCountryCoreId.pending, (state) => {
        state.tarhHayeSafar.loading = true;
      })
      .addCase(getActiveTarhHayeSafarByNoeGharardadSafarCoreIdAndCountryCoreId.fulfilled, (state, action) => {
        state.tarhHayeSafar.loading = false;
        if (action.payload !== undefined) {
          const list: ITarhSafarLabelValueType[] = action.payload?.map((item: any) => ({
            value: item.coreId,
            label: item.displayTitle,
            vahedPooli: item.vahedPooli,
            saghfeTaahodat: item.saghfeTaahodat,
            poosheshHa: item.poosheshHa,
          }));
          state.tarhHayeSafar.data = list;
        }
        return state;
      })
      .addCase(getActiveTarhHayeSafarByNoeGharardadSafarCoreIdAndCountryCoreId.rejected, (state) => {
        state.tarhHayeSafar.loading = false;
      });

    builder
      .addCase(getNoeGharardadSafarCountryByCountryCoreId.pending, (state) => {
        state.noeGharardadSafar.loading = true;
      })
      .addCase(getNoeGharardadSafarCountryByCountryCoreId.fulfilled, (state, action) => {
        state.noeGharardadSafar.loading = false;
        if (action.payload !== undefined) {
          state.noeGharardadSafar.data = action.payload?.map((c: any) => ({
            value: c.noeGharardadSafarCoreId,
            label: c.noeGharardadSafarDisplayTitle,
            noeBimeCoreId: c.noeBimehCoreId,
          }));
        }
        return state;
      })
      .addCase(getNoeGharardadSafarCountryByCountryCoreId.rejected, (state) => {
        state.noeGharardadSafar.loading = false;
      });

    builder
      .addCase(estelamMosaferati.pending, (state) => {
        state.estelam.loading = true;
      })
      .addCase(estelamMosaferati.fulfilled, (state, action) => {
        state.estelam.loading = false;
        state.isSuccess = false;
        state.estelam.result = null;
        if (action.payload !== undefined) {
          state.estelam.result = action.payload;
          state.isSuccess = true;
        }
        return state;
      })
      .addCase(estelamMosaferati.rejected, (state) => {
        state.estelam.loading = false;
      });
    builder
      .addCase(createShenasehPardakht.pending, (state) => {
        state.getCodeShenasehPardakhtMosaferatiResult.loading = true;
      })
      .addCase(createShenasehPardakht.fulfilled, (state, action) => {
        state.getCodeShenasehPardakhtMosaferatiResult.loading = false;

        if (action.payload && action.payload.status === 201) {
          state.getCodeShenasehPardakhtMosaferatiResult.data = action.payload.data;
          state.isSuccess = true;
        } else {
          state.isSuccess = false;
        }

        return state;
      })
      .addCase(createShenasehPardakht.rejected, (state) => {
        state.getCodeShenasehPardakhtMosaferatiResult.loading = false;
      });

    builder
      .addCase(getAddressWithPostCodeThunk.pending, (state) => {
        state.codeposti.loading = true;
      })
      .addCase(getAddressWithPostCodeThunk.fulfilled, (state) => {
        state.codeposti.loading = false;
      })
      .addCase(getAddressWithPostCodeThunk.rejected, (state) => {
        state.codeposti.loading = false;
      });

    builder
      .addCase(addBimehNamehMosaferatiToOrder.pending, (state) => {
        state.addToOrderResponse.loading = true;
      })
      .addCase(addBimehNamehMosaferatiToOrder.fulfilled, (state) => {
        state.addToOrderResponse.loading = false;
      })
      .addCase(addBimehNamehMosaferatiToOrder.rejected, (state) => {
        state.addToOrderResponse.loading = false;
      });

    builder
      .addCase(estelamSabtAhvalThunk.pending, (state) => {
        state.estelamSabtAhval.loading = true;
      })
      .addCase(estelamSabtAhvalThunk.fulfilled, (state) => {
        state.estelamSabtAhval.loading = false;
      })
      .addCase(estelamSabtAhvalThunk.rejected, (state) => {
        state.estelamSabtAhval.loading = false;
      });

    builder
      .addCase(estelamForeignNationalityThunk.pending, (state) => {
        state.estelamForeignNationality.loading = true;
        state.estelamForeignNationality.isSuccess = false;
      })
      .addCase(estelamForeignNationalityThunk.fulfilled, (state) => {
        state.estelamForeignNationality.loading = false;
        state.estelamForeignNationality.isSuccess = true;
      })
      .addCase(estelamForeignNationalityThunk.rejected, (state) => {
        state.estelamForeignNationality.loading = false;
        state.estelamForeignNationality.isSuccess = false;
      });
  },
});

export const {
  setBimehGozarData,
  setStepSixModel,
  setShowConfirmFromUser,
  setNoeGharardadSafar,
  setTarhHayeSafar,
  setTarhHayeSodoor,
  setTarafHayeGharardad,
  setBimehShodeh,
  clearBimehShode,
  clearSodoorCommand,
  clearGetCodeShenasehPardakhtMosaferatiResult,
  setIsMablaghKharidBimehNamehMoreThanHaghBimeh,
  setStepOneData,
  setBimehShodehData,
  setStepTwoData,
  clearEstelamResult,
  clearStepsData,
  setCommandData,
  setSodoorData,
  setHaghBimehPardakhtiCommand,
  wizardNextStep,
  wizardPreviousStep,
} = mosaferatiEstelamSlice.actions;

export default mosaferatiEstelamSlice.reducer;
